import { useEffect, useMemo, useRef, useState } from "react";
import { Blossom, Leaf } from "./Flora";
import { between, createRng, round } from "./rng";

/*
 * A bougainvillea toran across the top of the first page: swags of the pink flowers
 * used across the invitation, a knot where each pair of swags meets (the middle one
 * over the Ganesh emblem), a drape of warm fairy lights beneath, and strings of pearls
 * and flowers ending in brass bells hanging into the empty corners beside the title.
 *
 * It is laid out in real pixels for the width it is given (a little larger on tablets
 * and desktops), so the flowers keep their size and the number of swags follows the
 * width. It is always an even number, so a knot sits at the centre, above the emblem.
 */

const TOP = 4; // where the swags hang from, below the top edge
const SWAG_W = 170; // target width of one swag
const TITLE_MAX = 780; // the title column (styles/_hero.scss, .rm-hero__title)

// Drawn larger on wider screens, where the page has more room around the title
const scaleFor = (width) => (width < 600 ? 1 : width < 1100 ? 1.15 : 1.3);

// Points along a shallow catenary from (x0, TOP) to (x1, TOP), with arc lengths.
const sample = (x0, x1, sag, steps = 120) => {
  const pts = [];
  let length = 0;
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const p = [x0 + (x1 - x0) * t, TOP + sag * 4 * t * (1 - t)];
    if (i) length += Math.hypot(p[0] - pts[i - 1][0], p[1] - pts[i - 1][1]);
    pts.push(p);
    p.s = length;
  }
  return { pts, length };
};

// Evenly spaced points along a sampled curve, each with its unit normal (pointing down).
const along = ({ pts, length }, spacing, inset = 0) => {
  const out = [];
  let j = 1;
  for (let s = inset; s <= length - inset; s += spacing) {
    while (j < pts.length - 1 && pts[j].s < s) j += 1;
    const a = pts[j - 1];
    const b = pts[j];
    const f = (s - a.s) / Math.max(0.0001, b.s - a.s);
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const d = Math.hypot(dx, dy) || 1;
    out.push({ x: a[0] + dx * f, y: a[1] + dy * f, nx: -dy / d, ny: dx / d, t: s / length });
  }
  return out;
};

const pathOf = ({ pts }) => `M${pts.map(([x, y]) => `${round(x)} ${round(y)}`).join(" L")}`;
const leafFill = (rng) => (rng() > 0.5 ? "url(#rm-leaf)" : "url(#rm-leaf-dark)");
const deg = (rad) => (rad * 180) / Math.PI;

/**
 * Lays out every flower, leaf, light and string for a toran `width` units wide, drawn
 * at `scale` pixels per unit.
 */
const layout = (width, scale) => {
  const rng = createRng(83);
  const swags = Math.max(2, 2 * Math.round(width / (SWAG_W * 2)));
  const span = width / swags;
  const sag = Math.min(54, Math.max(34, span * 0.26));
  const roomy = width * scale >= 1100; // wide screens have tall empty corners beside the title
  const cx = width / 2;
  const titleHalf = Math.min(TITLE_MAX, width * scale - 36) / 2 / scale;

  const ropes = [];
  const wires = [];
  const leaves = [];
  const blooms = [];
  const bulbs = [];
  const knots = [];
  const drops = [];

  for (let k = 0; k < swags; k += 1) {
    const x0 = k * span;
    const x1 = x0 + span;
    const curve = sample(x0, x1, sag);
    ropes.push(pathOf(curve));

    // Leaves first, so the flowers sit over them: alternately above and below the rope,
    // pointing away from it.
    along(curve, 9, 4).forEach((p, i) => {
      const side = i % 2 ? 1 : -1;
      const out = deg(Math.atan2(p.ny * side, p.nx * side));
      leaves.push({
        x: p.x + p.nx * side * between(rng, 3, 7),
        y: p.y + p.ny * side * between(rng, 3, 7),
        length: between(rng, 10, 16),
        angle: out + between(rng, -38, 38),
        width: 0.36,
        fill: leafFill(rng),
      });
    });

    // The flowers: a rope of bracts, fullest at the bottom of each swag.
    along(curve, 5.2, 2).forEach((p) => {
      const fullness = 0.75 + 0.25 * Math.sin(Math.PI * p.t);
      const off = between(rng, -7, 7) * fullness;
      blooms.push({
        x: p.x + p.nx * off + between(rng, -1.5, 1.5),
        y: p.y + p.ny * off,
        size: between(rng, 6.5, 10) * fullness,
        angle: between(rng, 0, 120),
        gradient: Math.floor(rng() * 4),
      });
    });

    // A drape of fairy lights, hung a little lower than the flowers
    const wire = sample(x0, x1, sag + 24);
    wires.push(pathOf(wire));
    along(wire, 22, 14).forEach((p) => {
      bulbs.push({
        x: p.x,
        y: p.y + 2.5,
        duration: between(rng, 2.4, 4.2),
        delay: between(rng, -4, 0),
      });
    });
  }

  // A knot of flowers wherever two swags meet; the outer ends are tucked in from the
  // screen's edge. The middle knot (over the emblem) is the fullest.
  for (let k = 0; k <= swags; k += 1) {
    const edge = k === 0 || k === swags;
    const x = k === 0 ? 10 : k === swags ? width - 10 : k * span;
    const centre = k === swags / 2;
    const r = centre ? 12 : 8;
    const count = centre ? 8 : 6;
    const knot = { x, leaves: [], blooms: [] };
    for (let i = 0; i < 5; i += 1) {
      const a = -90 + (i - 2) * 44 + between(rng, -8, 8);
      knot.leaves.push({ x, y: TOP + 4, length: between(rng, 14, 19), angle: a + 180, width: 0.38, fill: leafFill(rng) });
    }
    for (let i = 0; i < count; i += 1) {
      const a = (i / count) * Math.PI * 2 + between(rng, -0.2, 0.2);
      knot.blooms.push({
        x: x + Math.cos(a) * r * between(rng, 0.7, 1),
        y: TOP + 5 + Math.sin(a) * r * 0.8 * between(rng, 0.7, 1),
        size: between(rng, 8.5, 11.5),
        angle: between(rng, 0, 120),
        gradient: Math.floor(rng() * 4),
      });
    }
    knot.blooms.push({ x, y: TOP + 5, size: centre ? 12 : 10, angle: between(rng, 0, 120), gradient: 3 });
    knots.push(knot);

    // Strings hang from every knot but the middle one (the emblem sits under it). Those
    // above the title are short; those at the edges and out in the corners are long.
    if (!centre) {
      const clear = edge || Math.abs(x - cx) > titleHalf + 14;
      const beads = !clear ? 3 : edge ? (roomy ? 16 : 13) : k % 2 ? (roomy ? 25 : 19) : roomy ? 19 : 15;
      drops.push({ x, y: TOP + 14, beads, bell: clear || k % 2 === 1, index: k });
    }
  }

  const deepest = drops.reduce((m, d) => Math.max(m, d.y + d.beads * 9 + 30), TOP + sag + 40);
  return { swags, ropes, wires, leaves, blooms, bulbs, knots, drops, height: Math.ceil(deepest) };
};

/** A string of pearls with a flower every few beads, ending in a brass bell or a pearl drop. */
const Drop = ({ x, y, beads, bell, index }) => {
  const end = y + beads * 9;
  return (
    <g
      className="rm-toran__drop"
      style={{ transformOrigin: `${round(x)}px ${y - 8}px`, "--sway": `${5 + (index % 3) * 0.9}s`, "--delay": `${-index * 0.7}s` }}
    >
      <line x1={round(x)} y1={y - 8} x2={round(x)} y2={end} stroke="#b78b4b" strokeWidth="0.8" />
      {Array.from({ length: beads }, (_, i) => {
        const by = y + i * 9 + 4;
        return i % 4 === 3 ? (
          <Blossom key={i} x={x} y={by} size={6} angle={i * 37} gradient={i % 4} />
        ) : (
          <g key={i}>
            <circle cx={round(x)} cy={by} r="2.6" fill="url(#rm-pearl)" />
            <circle cx={round(x - 0.8)} cy={by - 0.9} r="0.8" fill="#fff" opacity="0.9" />
          </g>
        );
      })}
      {bell ? (
        // A little brass ghungroo bell
        <g transform={`translate(${round(x)} ${end})`}>
          <circle cy="1.5" r="1.8" fill="url(#rm-gold)" />
          <path d="M0 2.6 C-5.4 2.6 -6 10 -7 15.5 L7 15.5 C6 10 5.4 2.6 0 2.6 Z" fill="url(#rm-brass)" />
          <rect x="-7.8" y="14.8" width="15.6" height="2.4" rx="1.2" fill="#87602c" />
          <circle cy="19.4" r="1.9" fill="#87602c" />
          <path d="M-3.4 6 C-3.6 9 -4 11.5 -4.6 13.6" stroke="#fff4cf" strokeWidth="0.9" strokeLinecap="round" fill="none" opacity="0.7" />
        </g>
      ) : (
        <g transform={`translate(${round(x)} ${end})`}>
          <path d="M0 0 C-3.2 4 -3.8 7.6 0 10.4 C3.8 7.6 3.2 4 0 0 Z" fill="url(#rm-pearl)" />
        </g>
      )}
    </g>
  );
};

/** Follows the element's width, so the toran can be laid out in real pixels. */
const useWidth = (ref) => {
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 390 : window.innerWidth));
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.round(entry.contentRect.width);
      if (next) setWidth(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
};

/*
 * Three layers: the lights and the strings move, so they are drawn apart from the
 * flowers, whose watercolour filter is costly to repaint.
 */
const Toran = ({ className = "" }) => {
  const ref = useRef(null);
  const width = useWidth(ref);
  const scale = scaleFor(width);
  const units = width / scale;
  const t = useMemo(() => layout(units, scale), [units, scale]);
  const height = Math.ceil(t.height * scale);
  const box = { viewBox: `0 0 ${round(units)} ${t.height}`, width, height, "aria-hidden": true, focusable: "false" };

  return (
    <div ref={ref} className={`rm-toran ${className}`} style={{ height }} aria-hidden="true">
      <svg className="rm-toran__lights" {...box}>
        <defs>
          <radialGradient id="rm-toran-glow">
            <stop offset="0%" stopColor="#fff6d2" stopOpacity="1" />
            <stop offset="30%" stopColor="#ffd56e" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#ffb940" stopOpacity="0" />
          </radialGradient>
        </defs>
        {t.wires.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="#8c6430" strokeOpacity="0.55" strokeWidth="0.8" />
        ))}
        {t.bulbs.map((b, i) => (
          <g key={i} transform={`translate(${round(b.x)} ${round(b.y)})`}>
            <circle
              className="rm-toran__glow"
              r="10"
              fill="url(#rm-toran-glow)"
              style={{ animationDuration: `${round(b.duration)}s`, animationDelay: `${round(b.delay)}s` }}
            />
            <circle r="2.3" fill="#fffbe8" stroke="#f2c25c" strokeWidth="0.6" />
          </g>
        ))}
      </svg>

      <svg className="rm-toran__drops" {...box}>
        {t.drops.map((d) => (
          <Drop key={d.index} {...d} />
        ))}
      </svg>

      <svg className="rm-toran__garland" {...box}>
        <g filter="url(#rm-watercolor)">
          {t.ropes.map((d, i) => (
            <path key={i} d={d} fill="none" stroke="#6b4a2a" strokeWidth="1.6" strokeLinecap="round" opacity="0.6" />
          ))}
          {t.leaves.map((leaf, i) => (
            <Leaf key={`l${i}`} {...leaf} />
          ))}
          {t.blooms.map((b, i) => (
            <Blossom key={`b${i}`} {...b} delay={0} />
          ))}
          {t.knots.map((knot, k) => (
            <g key={`k${k}`}>
              {knot.leaves.map((leaf, i) => (
                <Leaf key={i} {...leaf} />
              ))}
              {knot.blooms.map((b, i) => (
                <Blossom key={i} {...b} delay={0} />
              ))}
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
};

export default Toran;
